/** Pure helpers for the Site Register tab: London calendar days, display
 *  formatting and the CSV export. Site times are always shown in UK time,
 *  whatever zone the viewer's computer is set to, because that is the time
 *  on the site gate. Kept free of React so vitest covers every rule. */

import { toCsv } from '../../../utils/csvUtils';
import { slugifySegment } from '../../../utils/slug';
import type { SiteRegister } from '../../../types/siteAttendance';

export const SITE_TIME_ZONE = 'Europe/London';

const LONDON_WALL_CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: SITE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
const LONDON_CLOCK_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: SITE_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const LONDON_SHORT_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: SITE_TIME_ZONE,
  day: 'numeric',
  month: 'short',
});
// Calendar dates are formatted at UTC noon in UTC, so no zone can move them.
const DAY_LABEL = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function wallClock(ms: number) {
  const parts: Record<string, number> = {};
  for (const part of LONDON_WALL_CLOCK.formatToParts(new Date(ms))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return parts as { year: number; month: number; day: number; hour: number; minute: number; second: number };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function keyOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function parseKey(key: string): { year: number; month: number; day: number } {
  const match = DATE_KEY.exec(key);
  if (!match) throw new Error(`Not a calendar date: ${key}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** YYYY-MM-DD for the London calendar day containing `instant`. */
export function londonDateKey(instant: Date | number | string): string {
  const { year, month, day } = wallClock(new Date(instant).getTime());
  return keyOf(year, month, day);
}

/** Move a calendar date by whole days. */
export function shiftDateKey(key: string, days: number): string {
  const { year, month, day } = parseKey(key);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return keyOf(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** The instant London's clocks read 00:00 on a date. The UK changes its
 *  clocks at 01:00 UTC, never at midnight, so one correction is exact. */
function londonMidnight(key: string): Date {
  const { year, month, day } = parseKey(key);
  const utcMidnight = Date.UTC(year, month - 1, day);
  const offsetAt = (ms: number) => {
    const wall = wallClock(ms);
    return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second) - Math.floor(ms / 1000) * 1000;
  };
  const guess = utcMidnight - offsetAt(utcMidnight);
  return new Date(utcMidnight - offsetAt(guess));
}

/** A London day as [local midnight, next local midnight): 23 hours long
 *  when the clocks go forward and 25 when they go back. */
export function londonDayWindow(key: string): { from: Date; to: Date } {
  return { from: londonMidnight(key), to: londonMidnight(shiftDateKey(key, 1)) };
}

/** "Today", "Yesterday", or "Fri 2 Oct" (with the year when it is not this year's). */
export function formatDayLabel(key: string, todayKey: string): string {
  if (key === todayKey) return 'Today';
  if (key === shiftDateKey(todayKey, -1)) return 'Yesterday';
  const { year, month, day } = parseKey(key);
  const label = DAY_LABEL.format(new Date(Date.UTC(year, month - 1, day, 12)));
  // Appended by hand: en-GB puts a comma after the weekday once a year is in.
  return year === parseKey(todayKey).year ? label : `${label} ${year}`;
}

/** A calendar date as the day picker's Date: local midnight of that date,
 *  which is what react-day-picker compares against. */
export function dateKeyToPickerDate(key: string): Date {
  const { year, month, day } = parseKey(key);
  return new Date(year, month - 1, day);
}

export function pickerDateToDateKey(date: Date): string {
  return keyOf(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** The UK clock time of an instant, such as "07:02". */
export function formatLondonTime(instant: string): string {
  return LONDON_CLOCK_TIME.format(new Date(instant));
}

/** "07:02" for a time on `dayKey`, or "26 Sept 22:10" for one on another day,
 *  so an overnight visit never reads as ending before it began. */
export function formatClockTime(instant: string, dayKey: string): string {
  const time = formatLondonTime(instant);
  return londonDateKey(instant) === dayKey ? time : `${LONDON_SHORT_DATE.format(new Date(instant))} ${time}`;
}

/** "8h 50m", "8h", "45m" or "0m". */
export function formatDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** Hours as a decimal a spreadsheet can add up, such as "8.50". */
export function decimalHours(minutes: number): string {
  return (Math.max(0, minutes) / 60).toFixed(2);
}

function csvTime(instant: string, dayKey: string): string {
  const key = londonDateKey(instant);
  return key === dayKey ? formatLondonTime(instant) : `${key} ${formatLondonTime(instant)}`;
}

/** One row per visit: Name, Date, In, Out, Hours. The date is the day the
 *  visit started; a time on any other day carries its own date. A visit
 *  still in progress says "On site" and counts its hours so far. Unknown
 *  times and hours are left blank rather than guessed. */
export function buildSiteRegisterCsv(register: Pick<SiteRegister, 'people'>): string {
  const rows: (string | number)[][] = [['Name', 'Date', 'In', 'Out', 'Hours']];
  const people = [...register.people].sort((a, b) =>
    a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }) || a.userId.localeCompare(b.userId));
  for (const person of people) {
    for (const session of person.sessions) {
      const anchor = session.signInAt ?? session.signOutAt;
      const dayKey = anchor ? londonDateKey(anchor) : '';
      rows.push([
        person.name,
        dayKey,
        session.signInAt ? csvTime(session.signInAt, dayKey) : '',
        session.open ? 'On site' : session.signOutAt ? csvTime(session.signOutAt, dayKey) : '',
        session.minutes === null ? '' : decimalHours(session.minutes),
      ]);
    }
  }
  return toCsv(rows);
}

export function siteRegisterCsvFileName(projectName: string, dayKey: string): string {
  return `site-register-${slugifySegment(projectName)}-${dayKey}.csv`;
}
